import { TOURNAMENT_STATUS } from './constants.js';
import { createInitialBracket, createNextRound } from './tournamentUtils.js';

// Action types
export const ACTIONS = {
  INITIALIZE_TOURNAMENT: 'INITIALIZE_TOURNAMENT',
  SELECT_WINNER: 'SELECT_WINNER',
  NEXT_MATCH: 'NEXT_MATCH',
  RESTART_TOURNAMENT: 'RESTART_TOURNAMENT',
  SHOW_RANKINGS: 'SHOW_RANKINGS',
  HIDE_RANKINGS: 'HIDE_RANKINGS',
  SET_CAMPAIGN_DATA: 'SET_CAMPAIGN_DATA',
};

// Initial state factory
export function createInitialState(props) {
  if (props) {
    return {
      tournamentSize: 0,
      rounds: [],
      currentRound: 0,
      currentMatchIndex: 0,
      status: TOURNAMENT_STATUS.IN_PROGRESS,
      champion: null,
      originalCandidates: [],
      campaignData: null,
      ...props
    };
  }

  return {
    tournamentSize: 0,
    rounds: [],
    currentRound: 0,
    currentMatchIndex: 0,
    status: TOURNAMENT_STATUS.IN_PROGRESS,
    champion: null,
    originalCandidates: [],
    campaignData: null,
  };
}

// Reducer
export function tournamentReducer(state, action) {
  switch (action.type) {
    case ACTIONS.INITIALIZE_TOURNAMENT: {
      const { candidates, tournamentSize, props } = action.payload;
      const initialRound = createInitialBracket(candidates, tournamentSize);

      return {
        ...createInitialState(props),
        tournamentSize,
        rounds: [initialRound],
        currentRound: 0,
        currentMatchIndex: 0,
        status: TOURNAMENT_STATUS.IN_PROGRESS,
        originalCandidates: candidates,
      };
    }

    case ACTIONS.SELECT_WINNER: {
      const { winner } = action.payload;
      const newRounds = [...state.rounds];
      const currentRound = { ...newRounds[state.currentRound] };
      const currentMatch = { ...currentRound.matches[state.currentMatchIndex] };

      // Set winner for current match
      currentMatch.winner = winner;
      currentRound.matches = [...currentRound.matches];
      currentRound.matches[state.currentMatchIndex] = currentMatch;
      newRounds[state.currentRound] = currentRound;

      return {
        ...state,
        rounds: newRounds,
      };
    }

    case ACTIONS.NEXT_MATCH: {
      const currentRound = state.rounds[state.currentRound];
      const nextMatchIndex = state.currentMatchIndex + 1;

      // Check if current round is complete
      if (nextMatchIndex >= currentRound.matches.length) {
        // All matches in current round are complete
        // Check if all matches have winners
        const allMatchesComplete = currentRound.matches.every(
          match => match.winner !== null
        );

        if (!allMatchesComplete) {
          // Should not happen, but handle gracefully
          return state;
        }

        // Check if tournament is complete (only 1 match in round = finals)
        if (currentRound.matches.length === 1) {
          const finalMatch = currentRound.matches[0];
          const loser =
            finalMatch.candidate1.id === finalMatch.winner.id
              ? finalMatch.candidate2
              : finalMatch.candidate1;

          return {
            ...state,
            status: TOURNAMENT_STATUS.COMPLETED,
            champion: finalMatch.winner,
          };
        }

        // Create next round
        const nextRound = createNextRound(
          currentRound,
          state.currentRound + 2
        );

        if (!nextRound) {
          // Should not happen, but handle gracefully
          return state;
        }

        return {
          ...state,
          rounds: [...state.rounds, nextRound],
          currentRound: state.currentRound + 1,
          currentMatchIndex: 0,
        };
      }

      // Move to next match in current round
      return {
        ...state,
        currentMatchIndex: nextMatchIndex,
      };
    }

    case ACTIONS.RESTART_TOURNAMENT: {
      const initialRound = createInitialBracket(
        state.originalCandidates,
        state.tournamentSize
      );

      return {
        ...state,
        rounds: [initialRound],
        currentRound: 0,
        currentMatchIndex: 0,
        status: TOURNAMENT_STATUS.IN_PROGRESS,
        champion: null,
      };
    }

    case ACTIONS.SHOW_RANKINGS: {
      return {
        ...state,
        status: TOURNAMENT_STATUS.SHOWING_RANKINGS,
      };
    }

    case ACTIONS.HIDE_RANKINGS: {
      return {
        ...state,
        status: TOURNAMENT_STATUS.COMPLETED,
      };
    }

    case ACTIONS.SET_CAMPAIGN_DATA: {
      return {
        ...state,
        campaignData: action.payload,
      };
    }

    default:
      return state;
  }
}